// Analytics for one project (#/p/<id>/analytics): visits, time, heatmaps, rooms, places, devices, sources.
// Filters sit in one row above everything they scope; every chart has a table view.
import {
  getProject, openBilling, getPanoramas, getLevels, draftUrl, tourStats, tourPlaces, resetTourAnalytics, getAlternates, tourSchemes,
  type Studio, type TourStats, type NamedCount, type TourSchemes, unreadLeads } from '../data';
import { html, shell, wireJumps, toast, confirmDialog, setShellProject, $, type Raw } from '../ui';
import { statTile, barList, tableView, columnChart, chartBox, fmtNum, fmtTime, type ColumnPoint } from '../charts';
import { heatPlan, type Layer } from '../heatplan';

const RANGES = [
  { id: '7', label: '7 days', days: 7 },
  { id: '30', label: '30 days', days: 30 },
  { id: '90', label: '90 days', days: 90 },
  { id: '365', label: '12 months', days: 365 },
] as const;
type RangeId = typeof RANGES[number]['id'];

const store = {
  get: () => { try { return (localStorage.getItem('avr-range') as RangeId) || '30'; } catch { return '30' as RangeId; } },
  set: (v: string) => { try { localStorage.setItem('avr-range', v); } catch { /* storage off */ } },
};

/** From local midnight N-1 days ago until now. */
function rangeDates(id: RangeId): { since: Date; until: Date; label: string } {
  const r = RANGES.find((x) => x.id === id) ?? RANGES[1];
  const since = new Date(); since.setHours(0, 0, 0, 0); since.setDate(since.getDate() - (r.days - 1));
  return { since, until: new Date(Date.now() + 60_000), label: `previous ${r.label}` };
}

const regionNames = (() => { try { return new Intl.DisplayNames(['en-AU'], { type: 'region' }); } catch { return null; } })();
const countryName = (code: string | null | undefined) => (!code || code === '??' ? 'Unknown' : regionNames?.of(code) ?? code);
const deviceName = (d: string) => ({ phone: 'Phone', tablet: 'Tablet', desktop: 'Computer', unknown: 'Unknown' }[d] ?? d);
const sourceName = (r: string) => (r === 'direct' ? 'Direct or shared link' : r);
const when = (iso: string) => new Date(iso).toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });

/** Alternate panoramas: which finishes people looked at, and what they compared. */
function finishesBoxes(altCount: number, f: TourSchemes | null, name: (s: string) => string): Raw {
  if (!altCount) return html`<section class="box notice"><strong>Finishes</strong> Add alternate panoramas in Set up tour, such as a dark kitchen, and this shows which finishes buyers look at and compare.</section>`;
  const rows = (f?.schemes ?? []).map((r) => ({ label: name(r.name), value: r.seconds, display: fmtTime(r.seconds), sub: `${fmtNum(r.sessions)} visit${r.sessions === 1 ? '' : 's'}` }));
  const pairs = (f?.compares ?? []).map((r) => ({ label: `${name(r.a)} and ${name(r.b)}`, value: r.times, display: `${fmtNum(r.times)}×`, sub: fmtTime(r.seconds) }));
  return html`<div class="two">
    ${chartBox('Finishes people looked at', barList(rows, f ? 'No finishes looked at yet' : 'Fills in once people look at the published tour'),
      tableView(['Finish', 'Time', 'Visits', 'Times'], (f?.schemes ?? []).map((r) => [name(r.name), fmtTime(r.seconds), r.sessions, r.views])), 'Time standing at spots with alternate panoramas')}
    ${chartBox('Compared side by side', barList(pairs, 'No comparisons yet'),
      tableView(['Pair', 'Times', 'Time', 'Visits'], (f?.compares ?? []).map((r) => [`${name(r.a)} and ${name(r.b)}`, r.times, fmtTime(r.seconds), r.sessions])), 'With the before and after slider')}
  </div>`;
}

function filterRow(current: RangeId): Raw {
  return html`<div class="filter-row" role="group" aria-label="Date range">${RANGES.map((r) => html`<button data-range="${r.id}" aria-pressed="${r.id === current ? 'true' : 'false'}">${r.label}</button>`)}</div>`;
}

/** Views per day, or per week past 92 days. */
function viewSeries(s: TourStats): ColumnPoint[] {
  const day = (d: string) => new Date(`${d}T00:00:00`);
  if (s.days.length <= 92) {
    return s.days.map((d) => ({
      label: day(d.day).toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' }),
      short: day(d.day).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' }),
      value: d.views, extra: `${fmtNum(d.visitors)} visitor${d.visitors === 1 ? '' : 's'}`,
    }));
  }
  const weeks: ColumnPoint[] = [];
  for (let i = 0; i < s.days.length; i += 7) {
    const chunk = s.days.slice(i, i + 7);
    const start = day(chunk[0].day);
    weeks.push({
      label: `Week of ${start.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}`,
      short: start.toLocaleDateString('en-AU', { month: 'short' }),
      value: chunk.reduce((n, d) => n + d.views, 0),
    });
  }
  return weeks;
}

function kpis(s: TourStats, periodLabel: string, leadsAlways = false): Raw {
  return html`<div class="kpis">
    ${statTile('Views', fmtNum(s.views), s.views, s.prev.views, periodLabel)}
    ${statTile('Visitors', fmtNum(s.visitors), s.visitors, s.prev.visitors, periodLabel)}
    ${statTile('Average time in a tour', s.views ? fmtTime(s.avg_seconds) : '—', s.avg_seconds, s.prev.avg_seconds, periodLabel)}
    ${leadsAlways || s.leads ? statTile('Enquiries', fmtNum(s.leads)) : statTile('Total time in tours', s.seconds ? fmtTime(s.seconds) : '—')}
  </div>`;
}

const listRows = (rows: NamedCount[], name: (r: NamedCount) => string, sub?: (r: NamedCount) => string | undefined) =>
  rows.map((r) => ({ label: name(r), value: r.views, sub: sub?.(r) }));

function placesBox(s: TourStats): Raw {
  const rows = s.cities.length ? listRows(s.cities, (r) => r.name, (r) => countryName(r.country)) : listRows(s.countries, (r) => countryName(r.name));
  return chartBox('Where visitors are', barList(rows.slice(0, 8), 'No visits yet'),
    tableView(['Place', 'Views'], [...s.cities.map((c) => [`${c.name}, ${countryName(c.country)}`, c.views]), ...s.countries.map((c) => [countryName(c.name), c.views])]));
}
const devicesBox = (s: TourStats) => chartBox('Devices', barList(listRows(s.devices, (r) => deviceName(r.name)), 'No visits yet'),
  tableView(['Device', 'Views'], s.devices.map((d) => [deviceName(d.name), d.views])));
const sourcesBox = (s: TourStats) => chartBox('Where visitors came from', barList(listRows(s.referrers, (r) => sourceName(r.name)).slice(0, 8), 'No visits yet'),
  tableView(['Source', 'Views'], s.referrers.map((r) => [sourceName(r.name), r.views])), 'Sites with your tour embedded or linked');

function wireRange(page: HTMLElement, rerender: () => void): void {
  page.querySelectorAll<HTMLButtonElement>('[data-range]').forEach((b) => (b.onclick = () => {
    store.set(b.dataset.range!);
    page.querySelectorAll('[data-range]').forEach((x) => x.setAttribute('aria-pressed', String(x === b)));
    page.classList.add('refetch'); // keep the frame, dim it while the numbers reload
    rerender();
  }));
}

// ------------------------------------------------------------------ one tour

export async function projectAnalyticsView(root: HTMLElement, studio: Studio, projectId: string, section?: string): Promise<void> {
  // enquiries have their own page now; older links: rooms and places kept their ids
  if (section === 'enquiries') { location.hash = `#/p/${projectId}/enquiries`; return; }
  const page = shell(root, { studioName: studio.name, active: 'analytics', project: { id: projectId }, sub: section });
  let jumpTo = section; // only on first load; later re-renders (filters) stay where they are
  page.innerHTML = '<div class="loading-page">Loading analytics</div>';
  const project = await getProject(projectId);
  if (!project) { page.innerHTML = '<div class="empty"><h2>Project not found</h2></div>'; return; }
  setShellProject(root, project, () => openBilling(project.id), unreadLeads(project.id));
  const [panos, levels, alts] = await Promise.all([getPanoramas(projectId), getLevels(projectId), getAlternates(projectId).catch(() => [])]);
  const mainName = ((project.settings ?? {}) as { schemeDefault?: string }).schemeDefault?.trim() || 'Default';
  const finishName = (s: string) => (s === 'default' ? mainName : s);
  const levelIndex = new Map(levels.map((l, i) => [l.id, i]));
  const panoLabel = (id: string) => { const p = panos.find((x) => x.id === id); return p ? p.room || p.name : 'Removed render'; };

  let layerNow: Layer = 'time';
  const render = async () => {
    const range = store.get();
    const { since, until, label } = rangeDates(range);
    const head = html`<div class="page-head"><div><h1>Analytics</h1><p class="sub">${project.state === 'published' ? 'Visits to this tour’s share link. Your own views from the dashboard aren’t counted.' : 'This tour isn’t published right now, so no new visits are counted.'}</p></div>
      <button class="btn ghost-bad" id="reset">Reset analytics</button></div>`;
    let s: TourStats; let places;
    const finishes = alts.length ? await tourSchemes(project.id, since, until).catch(() => null) : null;
    try { [s, places] = await Promise.all([tourStats([project.id], since, until), tourPlaces(project.id, since, until)]); }
    catch (e) { page.classList.remove('refetch'); page.innerHTML = html`${head}<div class="empty box"><h2>Couldn’t load analytics</h2><p class="muted">${e instanceof Error ? e.message : ''}</p></div>`.html; return; }

    // time in each room: position samples (2 s each) counted against the nearest render, grouped by room name
    const visitsById = new Map(s.panos.map((p) => [p.id, p.sessions]));
    const rooms = new Map<string, { seconds: number; visits: number }>();
    for (const p of panos) {
      const key = p.room || p.name;
      const r = rooms.get(key) ?? { seconds: 0, visits: 0 };
      r.seconds += (places.panos[p.id] ?? 0) * 2;
      r.visits = Math.max(r.visits, visitsById.get(p.id) ?? 0);
      rooms.set(key, r);
    }
    const roomRows = [...rooms.entries()].filter(([, r]) => r.seconds > 0 || r.visits > 0).sort((a, b) => b[1].seconds - a[1].seconds)
      .map(([name, r]) => ({ label: name, value: r.seconds, display: fmtTime(r.seconds), sub: `${fmtNum(r.visits)} visit${r.visits === 1 ? '' : 's'}` }));
    const named = panos.some((p) => p.room);
    // most common moves, room to room (moves within one room are left out)
    const moveCount = new Map<string, number>();
    for (const [a, b, n] of places.flow) {
      const from = panoLabel(a); const to = panoLabel(b);
      if (from === to) continue;
      const k = `${from} → ${to}`;
      moveCount.set(k, (moveCount.get(k) ?? 0) + n);
    }
    const moveRows = [...moveCount.entries()].sort((a, b) => b[1] - a[1]).map(([label, value]) => ({ label, value }));
    const series = viewSeries(s);
    const actions: Array<[string, number]> = ([
      ['Opened the 3D plan', s.actions.mode_doll ?? 0],
      ['Switched to hop', s.actions.mode_hop ?? 0],
      ['Changed floor', s.actions.level ?? 0],
      ['Opened a hotspot', s.actions.hotspot_open ?? 0],
      ['Clicked a link', s.actions.link_click ?? 0],
      ['Saved a snapshot', s.actions.snapshot ?? 0],
      ['Shared the tour', s.actions.share ?? 0],
      ['Sent an enquiry', s.actions.lead ?? 0],
    ] as Array<[string, number]>).filter(([, n]) => n > 0);

    page.innerHTML = html`${head}${filterRow(range)}
      ${s.views ? '' : html`<div class="box notice"><strong>No visits in this period.</strong> Share the tour link and visits show up here within a minute.</div>`}
<i class="anchor" data-anchor="visits"></i>
      ${kpis(s, label, true)}
      ${chartBox(series.length > 92 ? 'Views per week' : 'Views per day', html`<div class="col-chart" id="views-chart"></div>`,
        tableView(['Date', 'Views', 'Visitors'], s.days.map((d) => [d.day, d.views, d.visitors]), false))}
<i class="anchor" data-anchor="heatmap"></i>
      ${chartBox('Heatmap', html`<div class="heat-plan" id="heat"></div>`, '', places.samples ? 'Hover a dot for the room' : 'Fills in once people walk around the published tour')}
      <i class="anchor" data-anchor="rooms"></i>
      <div class="two">
        ${chartBox('Time in each room', barList(roomRows, 'No time recorded yet'),
          tableView(['Room', 'Time', 'Visits'], roomRows.map((r) => [r.label, r.display, r.sub])),
          named ? '' : 'Name rooms on the Set up screen to group renders')}
        ${chartBox('Most common moves', barList(moveRows.slice(0, 8), 'No moves yet'),
          tableView(['Move', 'Times'], moveRows.map((r) => [r.label, r.value])), 'From one room to the next')}
      </div>
      <i class="anchor" data-anchor="finishes"></i>
      ${finishesBoxes(alts.length, finishes, finishName)}
      <i class="anchor" data-anchor="places"></i>
      <div class="two">
        ${chartBox('What visitors did', barList(actions.map(([l, n]) => ({ label: l, value: n, sub: s.views ? `${Math.round((n / s.views) * 100)}% of visits` : '' })), 'Nothing yet'),
          tableView(['Action', 'Visits'], actions.map(([l, n]) => [l, n])), 'Counted once per visit')}
        ${placesBox(s)}
      </div>
      <div class="two">${devicesBox(s)}${sourcesBox(s)}</div>
      ${chartBox('Recent visits', s.recent.length ? html`<table class="visits"><thead><tr><th>When</th><th>Where</th><th>Device</th><th>Time</th><th>Rooms</th></tr></thead><tbody>
          ${s.recent.slice(0, 12).map((v) => html`<tr><td>${when(v.started_at)}</td><td>${v.city ? `${v.city}, ` : ''}${countryName(v.country)}</td><td>${deviceName(v.device ?? 'unknown')}</td><td>${fmtTime(v.seconds)}</td><td>${v.panos}</td></tr>`)}
          </tbody></table>` : html`<p class="muted small">No visits yet</p>`)}
      <p class="muted small">Enquiries buyers sent are under <a href="#/p/${project.id}/enquiries">Enquiries</a>.</p>`.html;
    page.classList.remove('refetch');
    wireJumps(root, page, jumpTo); jumpTo = undefined;
    columnChart($('#views-chart', page), series, 'views');
    const heatHost = $('#heat', page);
    if (panos.length) {
      void heatPlan(heatHost, {
        modelUrl: draftUrl(project.id, project.model_key ?? 'model.glb'),
        levels: levels.map((l) => ({ name: l.name, height: +l.height })),
        panos: panos.map((p) => ({ id: p.id, label: panoLabel(p.id), x: +p.x, y: +p.y, z: +p.z, level: p.level_id ? levelIndex.get(p.level_id) ?? null : null, seconds: (places.panos[p.id] ?? 0) * 2, visits: visitsById.get(p.id) ?? 0 })),
        cells: places.cells, looks: places.looks ?? [], paths: places.paths ?? [], flow: places.flow ?? [],
      }, layerNow);
      heatHost.addEventListener('click', (e) => { const b = (e.target as HTMLElement).closest<HTMLElement>('[data-layer]'); if (b) layerNow = b.dataset.layer as Layer; });
    } else heatHost.innerHTML = '<p class="muted small">Upload renders to see the plan.</p>';
    wireRange(page, () => { void render(); });
    $('#reset', page).onclick = async () => {
      const ok = await confirmDialog({
        title: 'Reset analytics for this tour?',
        body: html`<p>Every visit recorded for <b>${project.name}</b> is deleted, so the numbers and heatmaps start from zero. Enquiries are kept.</p><p><b>This can’t be undone.</b></p>`,
        confirm: 'Reset analytics', danger: true, typeToConfirm: 'reset',
      });
      if (!ok) return;
      try { const n = await resetTourAnalytics(project.id); toast(`Analytics reset. ${n} visit${n === 1 ? '' : 's'} removed.`); void render(); }
      catch (e) { toast(e instanceof Error ? e.message : 'Couldn’t reset analytics', 'error', 8000); }
    };
  };
  await render();
}

